import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from 'antd';
import { useStore } from 'zustand';
import type { RenderNodesMap } from '@/types/commontypes';
import { ShapeType } from '@/types/commontypes';
import ShapeRenderComponent from './ShapeRender';
import { deleteShape } from '@/utils/deleteShape';
import { handleShapeMoveArrow } from '@/utils/KeyEvents';

import { Dialog } from '../ChartElements/dialog';
import type { OrderLineProps } from '@/types/commontypes';
import { getNextChartOrder } from '@/utils/chartHandlers';
import type { ChartId, RenderNode, MoreProps, RFCInteractiveItem, OrderLineRenderNode } from '../../types/commontypes';
import { shapeToCoordinates, coordinatesToShape } from '@/utils/coordinateConverter';
import { useChartShapes, useChartUIContext } from '../../contexts';
import { useGlobalShapeSelector } from '../../contexts/GlobalShapesContext';
import { globalShapeStore } from '../../stores/globalShapeStore';
import { useChartUIStoreSelector } from '../../stores/chartUIStore';
import {
  disableStateOnCancel,
  getOnlyNewShape,
  handleDraggedOrderLine,
  prepareNewShape,
  resetDrawingState,
  createRenderNodesMap,
  handleSelectedStatus,
  updateCoordinate,
  emptyMeasureArray,
} from '@/index';
import { useCandleData } from '@/hooks/use-candle-data';
import { calculateTPFromRatio, priceToNetRoe } from '@/utils/calculateSLndTP';
import { keys } from '@/utils/utils';

interface ShapeManagerProps {
  chartId: ChartId;
}

const ShapeManager: React.FC<ShapeManagerProps> = React.memo(({ chartId }) => {
  // Get state from contexts
  const shapeTypeToDraw = useGlobalShapeSelector(s => s.shapeTypeToDraw);
  const setShapeTypeToDraw = useGlobalShapeSelector(s => s.setShapeTypeToDraw);
  const { timeframe, drawState, setDrawState, shapeStore } = useChartShapes();
  const renderNodes = useStore(shapeStore, s => s.renderNodes);
  const setRenderNodes = useStore(shapeStore, s => s.setRenderNodes);

  // Get candle data from ChartCanvasContext
  const { fullData: candleData, xAccessor } = useCandleData();

  const setEnableShapeSelection = useGlobalShapeSelector(s => s.setEnableShapeSelection);
  const setSelectedShape = useGlobalShapeSelector(s => s.setSelectedShape);
  const selectedShape = useGlobalShapeSelector(s => s.selectedShape);

  const { chartUIStore, chartCanvasRef } = useChartUIContext();
  const setYCoordinateClicked = useChartUIStoreSelector(chartUIStore, s => s.setYCoordinateClicked);
  const setOverrideData = useChartUIStoreSelector(chartUIStore, s => s.setOverrideData);
  const setIsPopupVisible = useChartUIStoreSelector(chartUIStore, s => s.setIsPopupVisible);

  const [shapeOrderCounter, setShapeOrderCounter] = useState<number>(1);
  const shapeRefs = useRef<{ [key in ShapeType]?: unknown }>({});

  const [alertToEdit, setAlertToEdit] = useState<Partial<OrderLineProps>>();
  const [selectedItemUuid, setSelectedItemUuid] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [completePath, setCompletePath] = useState<boolean>(false);
  const [isAnyShapeEnabled, setIsAnyShapeEnabled] = useState(false);
  const [hoverIds, setHoverIds] = useState<Record<ChartId, string | null>>({});
  const hoverIdsRef = useRef<Record<ChartId, string | null>>({});
  /** 박스 겹침 시 동일 click 이벤트로 handleDeleteAlert가 중복 호출되는 것을 막는 플래그 */
  const handleShapeMoveRef = useRef<(direction: number) => void>(() => {});
  const handleShapeRef = useCallback(
    (type: ShapeType) => (node: unknown) => {
      if (node) {
        shapeRefs.current[type] = node;
        // saveRenderNodes removed - coordinates now stored in renderNodes
      }
    },
    [], // only recreate if chartId changes
  );

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      // 입력 요소에 포커스가 있으면 차트 단축키 무시
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const tag = (e.target as HTMLElement)?.tagName;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;

      // shapeStore.getState()로 최신 값 참조 (stale closure 방지)
      const storeState = shapeStore.getState();
      const { drawState, setRenderNodes } = storeState;

      switch (e.key) {
        case 'Delete': {
          if (!selectedShape) return;
          const { uuid, type, chartId: shapeChartId } = selectedShape;
          if (!uuid || !shapeChartId || !type) return;
          setRenderNodes((prev: RenderNodesMap) => deleteShape(uuid, prev, shapeChartId, type));
          shapeStore.getState().markDeleted(uuid);
          break;
        }

        case 'ArrowLeft':
        case 'ArrowRight': {
          const direction = e.key === 'ArrowLeft' ? -1 : 1;
          handleShapeMoveRef.current(direction);
          break;
        }
        case 'Escape':
          //complete path on right click or escape
          if (
            shapeTypeToDraw === ShapeType.PATH &&
            drawState?.type === ShapeType.PATH &&
            drawState?.currentPoints.length > 1
          ) {
            setCompletePath(true);
            break;
          }
          disableStateOnCancel(
            chartId,
            chartCanvasRef,
            shapeRefs,
            drawState,
            setDrawState,
            shapeTypeToDraw,
            setShapeTypeToDraw,
          );
          break;
        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartCanvasRef, chartId, shapeStore, selectedShape, setDrawState, setShapeTypeToDraw],
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      // shapeStore.getState()로 최신 값 참조 (stale closure 방지)
      const storeState = shapeStore.getState();
      const { drawState, renderNodes: currentRenderNodes } = storeState;

      // Right-click
      if (e.button === 2) {
        if (
          shapeTypeToDraw === ShapeType.PATH &&
          drawState?.type === ShapeType.PATH &&
          drawState?.currentPoints.length > 1
        ) {
          setCompletePath(true);
          return;
        }
        if (drawState?.status === 'started') {
          disableStateOnCancel(
            chartId,
            chartCanvasRef,
            shapeRefs,
            drawState,
            setDrawState,
            shapeTypeToDraw,
            setShapeTypeToDraw,
          );
        } else if (hoverIdsRef?.current?.[chartId]) {
          // If right-clicked without an active drawing, select shapes
          const hoveredUuid = hoverIdsRef.current[chartId];
          if (hoveredUuid) {
            // Find the shape type for the hovered shape
            const chartShapes = currentRenderNodes[chartId];
            if (chartShapes) {
              for (const [type, shapes] of Object.entries(chartShapes)) {
                if (Array.isArray(shapes) && shapes.find((s: { uuid: string }) => s.uuid === hoveredUuid)) {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                  setSelectedShape({ chartId, uuid: hoveredUuid, type: type as ShapeType, timeframe });
                  break;
                }
              }
            }
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartCanvasRef, chartId, shapeStore, setDrawState, setShapeTypeToDraw, setSelectedShape, timeframe],
  );

  useEffect(() => {
    hoverIdsRef.current = hoverIds;
  }, [hoverIds]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [chartId, handleKeyPress, handleMouseDown]);

  const handleShapeMove = (direction: number) => {
    if (selectedShape) {
      const { type, chartId } = selectedShape;
      if (!chartId) return;

      const updatedShapes = handleShapeMoveArrow(direction, selectedShape, renderNodes[chartId]?.[type] || []);

      const updatedShapesList = updateCoordinate(updatedShapes, candleData, timeframe, xAccessor, selectedShape.uuid);

      // Update shapes data structure with the new shape
      const updatedShapesStructure = createRenderNodesMap(renderNodes, updatedShapesList, type);
      setRenderNodes(updatedShapesStructure);
    }
  };
  handleShapeMoveRef.current = handleShapeMove;

  const onDoubleClick = useCallback(
    (_e: React.MouseEvent, _moreProps: MoreProps) => {
      const selected = globalShapeStore.getState().selectedShape;
      if (!selected) return;
      const { uuid, type } = selected;
      if (type === ShapeType.ORDER_LINE) {
        setShowModal(true);
        setSelectedItemUuid(uuid);
      } else if (uuid) {
        setIsPopupVisible?.(true);
      }
    },
    [setIsPopupVisible],
  );

  // Recalculate shape positions from coordinates when candleData/xAccessor change
  // This ensures shapes render at correct positions after chart remount or symbol change
  useEffect(() => {
    // Only run when candleData is available
    if (!candleData || candleData.length === 0 || !xAccessor) {
      return;
    }

    let needsUpdate = false;
    // 변경된 chartId만 새 참조를 생성하여, per-chartId 셀렉터가 불필요한 리렌더를 방지할 수 있도록 한다.
    const patches: Record<string, Record<string, RenderNode[]>> = {};

    for (const chartIdKey of keys(renderNodes)) {
      let chartChanged = false;
      const chartPatches: Record<string, RenderNode[]> = {};

      for (const type of keys(renderNodes[chartIdKey])) {
        const shapes = renderNodes[chartIdKey][type];
        if (!shapes || !Array.isArray(shapes)) continue;

        const mapped = shapes.map(shape => {
          // `coordinates`는 RenderNodeOf<S>에서 Omit되어 타입에는 없지만,
          // prepareNewShape / storageToRenderNode가 런타임에 `...shape` 스프레드로
          // StorageShape의 coordinates를 runtime 노드에 유지한다. 캔들 데이터가
          // 변경되었을 때(신규 캔들 추가 등) 안정적인 timestamp 기반 좌표로부터
          // 인덱스 기반 필드를 재계산하기 위해 런타임 필드를 사용한다.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const legacyCoords = (shape as unknown as { coordinates?: [number, number][] }).coordinates;
          // If shape has coordinates, recalculate position values
          if (legacyCoords && Array.isArray(legacyCoords) && legacyCoords.length > 0) {
            const recalculated = coordinatesToShape(legacyCoords, candleData, type, timeframe, xAccessor);
            if (Object.keys(recalculated).length > 0) {
              // Check if values actually changed
              // Cast to Record for dynamic key comparison — recalculated keys are coordinate fields
              // (start, end, startXY, endXY, points, etc.) that exist on the specific variant.
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              const shapeRecord = shape as unknown as Record<string, unknown>;
              const hasChanged = Object.keys(recalculated).some(key => {
                const oldVal = JSON.stringify(shapeRecord[key]);
                const newVal = JSON.stringify(recalculated[key]);
                return oldVal !== newVal;
              });
              if (hasChanged) {
                chartChanged = true;
                return { ...shape, ...recalculated };
              }
            }
          }
          return shape;
        }) as RenderNode[];

        chartPatches[type] = mapped;
      }

      if (chartChanged) {
        needsUpdate = true;
        patches[chartIdKey] = chartPatches;
      }
    }

    // 변경 사항이 있을 때만 업데이트 (무한 루프 방지: needsUpdate=false면 setRenderNodes 호출하지 않음)
    if (needsUpdate) {
      setRenderNodes(prev => {
        const next = { ...prev };
        for (const [cid, chartPatches] of Object.entries(patches)) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          next[cid as ChartId] = chartPatches;
        }
        return next;
      });
    }
  }, [candleData, xAccessor, timeframe, setRenderNodes, renderNodes]);

  // isShapeSelected is now derived from renderNodes via useMemo in ChartShapesContext
  // No need for useEffect sync - updates happen in the same render cycle

  const handleChoosePosition = useCallback(
    (_e: React.MouseEvent, text: RenderNode[], _moreProps: MoreProps) => {
      const currentRenderNodes = shapeStore.getState().renderNodes;
      const updatedShapesArray = text.map(shape =>
        prepareNewShape(
          { ...shape, order: shape.order || shapeOrderCounter },
          currentRenderNodes,
          candleData,
          timeframe,
          xAccessor,
          ShapeType.TEXT,
        ),
      );
      setShapeOrderCounter(updatedShapesArray.length > 0 ? shapeOrderCounter + 1 : shapeOrderCounter);

      const updatedShapesData = createRenderNodesMap(currentRenderNodes, updatedShapesArray, ShapeType.TEXT);
      setRenderNodes(updatedShapesData);
      // 새로 추가된 Text 도형들을 dirty로 표시 — 사용자가 popup에서 텍스트를 변경하지 않고
      // 기본값('Enter Text') 그대로 두더라도 upsert가 트리거되도록 보장.
      for (const shape of updatedShapesArray) {
        shapeStore.getState().markDirty(shape.uuid);
      }
      handleSelectedStatus(chartId, ShapeType.TEXT, updatedShapesArray, timeframe, setSelectedShape);
      setIsPopupVisible(true);
      resetDrawingState(
        ShapeType.TEXT,
        setShapeTypeToDraw,
        setEnableShapeSelection,
        setYCoordinateClicked,
        setOverrideData,
        setDrawState,
      );
      setEnableShapeSelection(false);
    },
    [
      shapeStore,
      chartId,
      candleData,
      timeframe,
      xAccessor,
      shapeOrderCounter,
      setRenderNodes,
      setIsPopupVisible,
      setSelectedShape,
      setShapeTypeToDraw,
      setEnableShapeSelection,
      setYCoordinateClicked,
      setOverrideData,
      setDrawState,
    ],
  );

  const handleDialogClose = () => {
    setShowModal(false);
  };

  const onDrawComplete = useCallback(
    (_event: React.MouseEvent, shapesList: RenderNode[], type: ShapeType) => {
      const newShape = getOnlyNewShape(shapesList);
      if (!newShape) return;
      const currentRenderNodes = shapeStore.getState().renderNodes;
      const shapeChartId = newShape.chartId;
      const currentShapes = currentRenderNodes?.[shapeChartId]?.[type] || [];
      const preparedShape = prepareNewShape(newShape, currentRenderNodes, candleData, timeframe, xAccessor, type);
      const updatedShapesArray = type === ShapeType.MEASURE ? [preparedShape] : [...currentShapes, preparedShape];
      if (preparedShape.order != null) setShapeOrderCounter(preparedShape.order);

      const updatedShapesStructure = createRenderNodesMap(currentRenderNodes, updatedShapesArray, type);
      setRenderNodes(updatedShapesStructure);
      shapeStore.getState().markDirty(preparedShape.uuid);
      // 새로 생성된 도형을 직접 선택 (selected 플래그에 의존하지 않음)
      setSelectedShape({
        chartId: shapeChartId,
        uuid: preparedShape.uuid,
        timeframe,
        type,
        mode: 'create',
      });
      resetDrawingState(
        type,
        setShapeTypeToDraw,
        setEnableShapeSelection,
        setYCoordinateClicked,
        setOverrideData,
        setDrawState,
      );
    },
    [
      shapeStore,
      candleData,
      timeframe,
      xAccessor,
      setRenderNodes,
      setSelectedShape,
      setShapeTypeToDraw,
      setEnableShapeSelection,
      setYCoordinateClicked,
      setOverrideData,
      setDrawState,
    ],
  );

  const onDragComplete = useCallback(
    (_event: React.MouseEvent, newShapes: RenderNode[], type: ShapeType, dragged?: boolean) => {
      if (type === ShapeType.ORDER_LINE) {
        handleDraggedOrderLine(newShapes, dragged, setAlertToEdit);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const draggedUuid = newShapes.find(s => (s as unknown as RFCInteractiveItem).selected)?.uuid;
        // eslint-disable-next-line no-param-reassign
        newShapes = newShapes.map(shape => {
          if (shape.type !== ShapeType.ORDER_LINE) return shape;
          // 드래그된 shape만 TP 자동 계산 — 다른 shape의 tp=0은 사용자가 의도적으로 삭제한 것일 수 있음
          if (shape.uuid !== draggedUuid) return shape;
          const ol = shape;
          if (!ol.tpslPrice || ol.tpslPrice.length === 0) return shape;
          const dir = ol.positionType === 'short' ? 'short' : 'long';
          const ratio = globalShapeStore.getState().lastTPSLByDirection[dir] ?? 2.0;
          const amount = ol.amount ?? 0;
          const entry = ol.yValue ?? 0;
          const positionType = ol.positionType ?? 'long';
          const updatedTpsl = ol.tpslPrice.map(pair => {
            if (pair.tp !== 0 && pair.tp !== ol.yValue) return pair; // tp already set (not at entry price)
            if (pair.sl === 0 || pair.sl === ol.yValue) return pair; // sl not set — cannot calculate tp
            if (amount <= 0) return pair;
            try {
              const tp = calculateTPFromRatio({
                TP_SL_ratio: ratio,
                price: entry,
                amount,
                sl: pair.sl,
                positionType,
              });
              return { ...pair, tp };
            } catch {
              return pair;
            }
          });
          const updatedTpslNetRoe = updatedTpsl.map(pair => ({
            tp: pair.tp !== 0 ? priceToNetRoe({ price: pair.tp, entry, amount, positionType }) : 0,
            sl: pair.sl !== 0 ? priceToNetRoe({ price: pair.sl, entry, amount, positionType }) : 0,
          }));

          return { ...shape, tpslPrice: updatedTpsl, tpsl: updatedTpslNetRoe };
        });
      }
      const currentRenderNodes = shapeStore.getState().renderNodes;
      const updatedChartId = newShapes.at(0)?.chartId || chartId;
      // Extract selected shape uuid from RFC callback data (selected flag is on RFCInteractiveItem, not RenderNode)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const dragSelectedUuid = newShapes.find(s => (s as unknown as RFCInteractiveItem).selected)?.uuid;
      const updatedShapesList = updateCoordinate(newShapes, candleData, timeframe, xAccessor, dragSelectedUuid);
      const updatedShapesStructure = createRenderNodesMap(currentRenderNodes, updatedShapesList, type);

      setRenderNodes(updatedShapesStructure);
      if (dragSelectedUuid) {
        shapeStore.getState().markDirty(dragSelectedUuid);
      }
      // ORDER_LINE 드래그 완료 시 selected 상태를 설정하지 않음 — 드래그 후 서클이 영구 표시되는 문제 방지
      if (type !== ShapeType.ORDER_LINE) {
        handleSelectedStatus(updatedChartId, type, newShapes, timeframe, setSelectedShape);
      }
      resetDrawingState(
        type,
        setShapeTypeToDraw,
        setEnableShapeSelection,
        setYCoordinateClicked,
        setOverrideData,
        setDrawState,
      );
    },
    [
      shapeStore,
      chartId,
      candleData,
      timeframe,
      xAccessor,
      setRenderNodes,
      setSelectedShape,
      setShapeTypeToDraw,
      setEnableShapeSelection,
      setYCoordinateClicked,
      setOverrideData,
      setDrawState,
    ],
  );

  const handleStart = useCallback(
    (_e: React.MouseEvent, newShape: [number, number] | [number, number][], moreProps: MoreProps, type: ShapeType) => {
      setOverrideData({});
      if (type === ShapeType.MEASURE) return;
      if (
        type === ShapeType.PATH ||
        type === ShapeType.ELLIOTT_IMPULSE_12345 ||
        type === ShapeType.ELLIOTT_CORRECTION_ABC ||
        type === ShapeType.XABCD_PATTERN
      ) {
        setDrawState({
          type,
          chartId: moreProps.chartId || chartId,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          currentPoints: newShape as [number, number][],
          status: 'started',
          endXY: [0, 0],
        });
      } else {
        setDrawState({
          type,
          chartId: moreProps.chartId || chartId,
          status: 'started',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          startXY: newShape as [number, number],
        });
      }
    },
    [chartId, setOverrideData, setDrawState],
  );

  const onDragTextComplete = useCallback(
    (_e: React.MouseEvent, textList: RenderNode[], _moreProps: MoreProps) => {
      const currentRenderNodes = shapeStore.getState().renderNodes;
      const nextOrder = getNextChartOrder(currentRenderNodes, chartId);
      const updatedShapesArray = textList.map(shape =>
        prepareNewShape(shape, currentRenderNodes, candleData, timeframe, xAccessor, ShapeType.TEXT),
      );

      const updatedShapesData = createRenderNodesMap(currentRenderNodes, updatedShapesArray, ShapeType.TEXT);
      setRenderNodes(updatedShapesData);
      handleSelectedStatus(chartId, ShapeType.TEXT, updatedShapesArray, timeframe, setSelectedShape);
      setShapeOrderCounter(nextOrder);
      resetDrawingState(
        ShapeType.TEXT,
        setShapeTypeToDraw,
        setEnableShapeSelection,
        setYCoordinateClicked,
        setOverrideData,
        setDrawState,
      );
    },
    [
      shapeStore,
      chartId,
      candleData,
      timeframe,
      xAccessor,
      setRenderNodes,
      setSelectedShape,
      setShapeTypeToDraw,
      setEnableShapeSelection,
      setYCoordinateClicked,
      setOverrideData,
      setDrawState,
    ],
  );

  // Function to handle updates when a orderline shape is modified
  const handleChangeAlert = (updatedAmount: OrderLineProps) => {
    const updatedOrderLineShapes =
      renderNodes[chartId]?.[ShapeType.ORDER_LINE]?.map(item => {
        if (item.uuid === selectedItemUuid) {
          const updatedItem = {
            ...item,
            amount: updatedAmount.amount,
            yValue: updatedAmount.price,
            tpsl: updatedAmount.tpsl,
          };
          return prepareNewShape(updatedItem, renderNodes, candleData, timeframe, xAccessor, ShapeType.ORDER_LINE); // Update coordinates
        }
        return item;
      }) || [];

    // Update shapes data
    const updatedShapesData = createRenderNodesMap(
      renderNodes,

      updatedOrderLineShapes as RenderNode[],
      ShapeType.ORDER_LINE,
    );
    setRenderNodes(updatedShapesData);
    shapeStore.getState().markDirty(selectedItemUuid);

    // Reset UI state
    setAlertToEdit(updatedAmount);
    setShowModal(false);
    resetDrawingState(
      ShapeType.ORDER_LINE,
      setShapeTypeToDraw,
      setEnableShapeSelection,
      setYCoordinateClicked,
      setOverrideData,
      setDrawState,
    );
  };

  const handleDeleteAlert = useCallback(
    (yCoordinate: OrderLineRenderNode, type: 'Entry' | 'TP' | 'SL') => {
      if (!yCoordinate) return;

      const doDeleteEntry = () => {
        const updatedData: RenderNodesMap = { ...shapeStore.getState().renderNodes };
        if (!updatedData[chartId]) updatedData[chartId] = {};
        if (!updatedData[chartId][ShapeType.ORDER_LINE]) updatedData[chartId][ShapeType.ORDER_LINE] = [];
        const currentOrders = updatedData[chartId][ShapeType.ORDER_LINE];
        const remaining = currentOrders.filter(d => d.uuid !== yCoordinate.uuid);
        updatedData[chartId] = { ...updatedData[chartId], [ShapeType.ORDER_LINE]: remaining };

        // 헷지 → 단방향 전환: 삭제 전 롱+숏이 모두 있었고 삭제 후 한 방향만 남으면
        // 남은 방향의 비율을 hedge 비율로 덮어씌움
        const wasHedge =
          currentOrders.some(d => d.positionType === 'long') && currentOrders.some(d => d.positionType === 'short');
        const stillHasLong = remaining.some(d => d.positionType === 'long');
        const stillHasShort = remaining.some(d => d.positionType === 'short');
        if (wasHedge && !(stillHasLong && stillHasShort)) {
          const { lastTPSLByDirection } = globalShapeStore.getState();
          const survivingDir = stillHasLong ? 'long' : 'short';
          globalShapeStore.getState().updateLastTPSLByDirection(survivingDir, lastTPSLByDirection.hedge);
        }

        setRenderNodes(updatedData);
        shapeStore.getState().markDeleted(yCoordinate.uuid);
      };

      const updatedData: RenderNodesMap = { ...shapeStore.getState().renderNodes };
      if (!updatedData[chartId]) updatedData[chartId] = {};
      if (!updatedData[chartId][ShapeType.ORDER_LINE]) updatedData[chartId][ShapeType.ORDER_LINE] = [];
      const currentOrders = updatedData[chartId][ShapeType.ORDER_LINE];
      switch (type) {
        case 'Entry': {
          // 체결 상태(isEntered)이면 강제 청산 확인 후 삭제
          if (yCoordinate.isEntered === true) {
            const label = yCoordinate.positionType === 'long' ? '롱' : '숏';
            Modal.confirm({
              title: '강제 청산',
              content: `${label} 포지션을 강제 청산하시겠습니까?`,
              okText: 'OK',
              cancelText: 'Cancel',
              okButtonProps: { danger: true },
              onOk: doDeleteEntry,
            });
            return;
          }
          const remaining = currentOrders.filter(d => d.uuid !== yCoordinate.uuid);
          updatedData[chartId] = { ...updatedData[chartId], [ShapeType.ORDER_LINE]: remaining };

          // 헷지 → 단방향 전환: 삭제 전 롱+숏이 모두 있었고 삭제 후 한 방향만 남으면
          // 남은 방향의 비율을 hedge 비율로 덮어씌움
          const wasHedge =
            currentOrders.some(d => d.positionType === 'long') && currentOrders.some(d => d.positionType === 'short');
          const stillHasLong = remaining.some(d => d.positionType === 'long');
          const stillHasShort = remaining.some(d => d.positionType === 'short');
          if (wasHedge && !(stillHasLong && stillHasShort)) {
            const { lastTPSLByDirection } = globalShapeStore.getState();
            const survivingDir = stillHasLong ? 'long' : 'short';
            globalShapeStore.getState().updateLastTPSLByDirection(survivingDir, lastTPSLByDirection.hedge);
          }
          shapeStore.getState().markDeleted(yCoordinate.uuid);
          break;
        }

        case 'TP':
        case 'SL':
          // TP든 SL이든 X 버튼 → tpslPrice 전체 삭제
          updatedData[chartId] = {
            ...updatedData[chartId],
            [ShapeType.ORDER_LINE]: currentOrders.map(d => {
              if (d.uuid === yCoordinate.uuid) {
                const updated = { ...d, tpslPrice: [], tpsl: [] };
                return {
                  ...updated,
                  coordinates: shapeToCoordinates(updated, candleData, timeframe, xAccessor),
                };
              }
              return d;
            }),
          };
          shapeStore.getState().markDirty(yCoordinate.uuid);
          break;

        default:
          break;
      }
      setRenderNodes(updatedData);
      setShowModal(false);
    },
    [shapeStore, chartId, candleData, timeframe, xAccessor, setRenderNodes],
  );

  const handleDeleteAllOrderLines = useCallback(() => {
    const doDeleteAll = () => {
      const updatedData: RenderNodesMap = { ...shapeStore.getState().renderNodes };
      if (!updatedData[chartId]) updatedData[chartId] = {};
      const existing = updatedData[chartId][ShapeType.ORDER_LINE] ?? [];
      for (const ol of existing) {
        shapeStore.getState().markDeleted(ol.uuid);
      }
      updatedData[chartId][ShapeType.ORDER_LINE] = [];
      setRenderNodes(updatedData);
    };

    const orderLines = shapeStore.getState().renderNodes[chartId]?.[ShapeType.ORDER_LINE] ?? [];
    const enteredLines = orderLines.filter(d => d.isEntered === true);

    if (enteredLines.length > 0) {
      const labels = enteredLines.map(d => (d.positionType === 'long' ? '롱' : '숏')).join(', ');
      Modal.confirm({
        title: '강제 청산',
        content: `체결된 ${labels} 포지션이 포함되어 있습니다. 강제 청산 후 모든 오더라인을 삭제하시겠습니까?`,
        okText: 'OK',
        cancelText: 'Cancel',
        okButtonProps: { danger: true },
        onOk: doDeleteAll,
      });
      return;
    }

    doDeleteAll();
  }, [shapeStore, chartId, setRenderNodes]);

  // Effect: When a drawing tool is selected from the toolbar, deselect any existing shape.
  // Only reacts to `shapeTypeToDraw` changes (toolbar tool selection),
  // NOT to `selectedShape` changes (shape click selection).
  useEffect(() => {
    if (shapeTypeToDraw) {
      // A drawing tool was activated — deselect any currently selected shape
      setSelectedShape(null);
    }
    if (shapeTypeToDraw === ShapeType.MEASURE) {
      setRenderNodes(prev => emptyMeasureArray(prev));
    }
    setIsAnyShapeEnabled(shapeTypeToDraw != null);
  }, [shapeTypeToDraw, setRenderNodes, setIsAnyShapeEnabled, setSelectedShape]);

  //  drawingObjectMap - not used anymore, kept for reference
  // const drawingObjectMap = {
  //   TrendLine: 'Trendline',
  //   FibRetracement: 'FibonacciRetracement',
  //   FibChannel: 'EquidistantChannel',
  //   GannBox: 'GannBox',
  //   GannFan: 'GannFan',
  //   InteractiveText: "textList",
  //   Ray: "Ray",
  //   InfoLine: "InfoLine",
  //   HorzLine: "HorzLine",
  //   VertLine: "VertLine",
  //   ElliottImpulse12345: "ElliottImpulse12345",
  //   XABCDPattern: 'XABCDPattern',
  //   Measure: "measure",
  //   Rectangle: "Rectangle",
  //   Triangle: "Triangle",
  //   Path: "Path",
  //   ElliottCorrectionABC: "ElliottCorrectionABC",
  //   ParallelChannel: "ParallelChannel",
  //   InteractiveOrderLine: "orderLineList"
  // };

  return (
    <div>
      <ShapeRenderComponent
        onChoosePostion={handleChoosePosition}
        onDrawComplete={onDrawComplete}
        onStart={handleStart}
        onDragTextComplete={onDragTextComplete}
        handleShapeRef={handleShapeRef}
        onDoubleClick={onDoubleClick}
        handleDeleteAlert={handleDeleteAlert}
        handleDeleteAllOrderLines={handleDeleteAllOrderLines}
        chartId={chartId}
        sharedState={drawState}
        setSharedState={setDrawState}
        onDragComplete={onDragComplete}
        completePath={completePath}
        isAnyShapeEnabled={isAnyShapeEnabled}
        setHoverIds={setHoverIds}
        hoverIds={hoverIds}
      />
      {/* DrawingObjectSelector removed - not used (no onSelect/onDoubleClick handlers) */}
      {/* <DrawingObjectSelector
        enabled={drawingEnabled}
        getRenderNodes={getRenderNodes}
        drawingObjectMap={drawingObjectMap}
      /> */}

      {/* ShapePropertyPopup is now rendered in CandlestickChart for the selected chart only */}

      {showModal && shapeTypeToDraw === ShapeType.ORDER_LINE && (
        <Dialog
          showModal={showModal}
          onClose={handleDialogClose}
          onSave={handleChangeAlert}
          defaultValues={{
            amount: alertToEdit?.amount,
            price: alertToEdit?.price ?? 0,
            tpsl: alertToEdit?.tpsl,
            positionType: alertToEdit?.positionType,
            type: ShapeType.ORDER_LINE,
          }}
        />
      )}
    </div>
  );
});

ShapeManager.displayName = 'ShapeManager';
export default ShapeManager;
